
import {
  computed,
  defineComponent,
  nextTick,
  onMounted,
  PropType,
  ref,
  toRef,
  watch,
} from "vue";
import { useStore } from "vuex";
import { Form, Field, ErrorMessage, FieldArray } from "vee-validate";
import * as Yup from "yup";
import { useI18n } from "vue-i18n/index";
import { useToast } from "vue-toastification";
import { SiteApi } from "@/services/api/Site.service";
import { getMaxLengthErrorMessage } from "@/helpers/data-format";
export default defineComponent({
  name: "add-address-modal",
  emits: ["update:modelValue", "submit"],
  props: {
    data: { type: Object as PropType<any>, required: true },
    // countries: { type: Array as PropType<any>, required: true },
    showModal: { type: Boolean },
    isEditable: { type: Boolean },
  },
  components: {
    Form,
    Field,
    ErrorMessage,
  },
  setup(props, { emit }) {
    const { t, te } = useI18n();
    const translate = (key: string) => {
      if (te(key)) {
        return t(key);
      } else {
        return key.split(".").join(" ");
      }
    };

    const store = useStore();
    const toast = useToast();
    const clientDetails = computed(
      () => store.getters.getClientDetails.content
    );
    const userAccountId = ref<any>();
    const siteId = ref<number>();
    const isVisible = ref<boolean>(false);

    const closeButtonRef = ref<null | HTMLButtonElement>();
    const userRoleId = ref<any>();
    const countryOptions = ref<Array<{ label: any; value: string }>>([]);
    const stateOptions = ref<Array<{ label: any; value: string }>>([]);
    const cityOptions = ref<Array<{ label: any; value: string }>>([]);
    let userOptions: { label: any; value: string }[] = [];
    let roleOptions = ref<any>([]);
    const dataProp = toRef(props, "data");
    // const countryOptions = toRef(props, "countries");
    const modelValueProp = toRef(props, "showModal");
    const editable = toRef(props, "isEditable");
    const addressLine1 = ref<any>("");
    const addressLine2 = ref<any>("");
    const city = ref<any>("");
    const zip = ref<any>("");
    const country = ref<any>("");
    const countryId = ref<any>("");
    const state = ref<any>("");
    const hideModalRef = ref<null | HTMLElement>();
    const formRef = ref<null | HTMLFormElement>(null);
    let siteUserTargetData = ref<any>({});
    const contactDetailsValidation = Yup.object().shape({
      country: Yup.string()
        .strict(true)
        .required("Country is required")
        .matches(/\S/, "Country is required"),
      street: Yup.string()
        .strict(true)
        .max(100, getMaxLengthErrorMessage(t("pages.user.addressLine1"), 100))
        .required(t("pages.user.addressLine1") + " " + t("common.isRequired"))
        .matches(
          /\S/,
          t("pages.user.addressLine1") + " " + t("common.isRequired")
        ),
      street2: Yup.string().max(
        100,
        getMaxLengthErrorMessage(t("pages.user.addressLine2"), 100)
      ),
      city: Yup.string().when([], {
        is: () => cityOptions.value.length === 0,
        then: (schema) => schema,
        otherwise: (schema) =>
          schema.required(t("pages.user.city") + " " + t("common.isRequired")),
      }),
      state: Yup.string().when([], {
        is: () => stateOptions.value.length === 0,
        then: (schema) => schema,
        otherwise: (schema) =>
          schema.required(t("pages.user.state") + " " + t("common.isRequired")),
      }),
      postalCode: Yup.string()
        .strict(true)
        .max(10, getMaxLengthErrorMessage(t("pages.user.zip"), 10))
        .required(t("pages.user.zip") + " " + t("common.isRequired"))
        .matches(/\S/, t("pages.user.zip") + " " + t("common.isRequired")),
    });
    const loading = ref<boolean>(false);
    const convertToCapitalCase = () => {
      zip.value = zip.value.toUpperCase();
    };
    const setDefaultCountry = async () => {
      if (clientDetails.value) {
        const clientCountry = clientDetails.value[0].addressModel.country;

        if (clientCountry && !country.value && !countryId.value) {
          // Try by label then ID for robustness
          let validCountry = countryOptions.value.find(
            (c) => c.label === clientCountry
          );
          if (!validCountry) {
            validCountry = countryOptions.value.find(
              (c) => c.value === clientCountry
            );
          }
          if (validCountry) {
            country.value = validCountry.label;
            countryId.value = validCountry.value;
            await loadState(validCountry.value, false);
          }
        }
      }
    };
    onMounted(async () => {
      try {
        const data = await SiteApi.getInstance().getCountries();
        countryOptions.value = data.map((item) => ({
          label: item.text,
          value: item.value,
        }));
        if (countryOptions.value.length > 0) {
          await nextTick();
          await setDefaultCountry();
        }
      } catch (err) {
        console.error("Failed to load countries:", err);
      }
    });

    const initialValues = computed(() => {
      return {
        street: addressLine1.value,
        street2: addressLine2.value,
        country: country.value,
        state: state.value,
        city: city.value,
        postalCode: zip.value,
      };
    });

    const changeCountry = async (value) => {
      state.value = "";
      city.value = "";
      stateOptions.value = [];
      cityOptions.value = [];
      const selectedCountry = countryOptions.value.find(
        (c) => c.value === value
      );

      if (selectedCountry?.value) {
        country.value = selectedCountry.label;
        countryId.value = selectedCountry.value;
        await loadState(selectedCountry?.value, false);
      } else {
        country.value = "";
        countryId.value = "";
      }
    };

    const changeState = async (value) => {
      city.value = "";
      const selectedState = stateOptions.value.find((c) => c.label == value);
      if (selectedState?.value) {
        await loadCity(selectedState?.value);
      }
    };
    const loadCity = async (value) => {
      cityOptions.value = [];
      try {
        const data = await SiteApi.getInstance().getCities(value);
        cityOptions.value = data.map((item) => ({
          label: item.text,
          value: item.value,
        }));
      } catch (err) {
        console.error("Failed to load cities:", err);
      }
    };
    const loadState = async (value, edit) => {
      stateOptions.value = [];
      if (!edit) {
        state.value = "";
        city.value = "";
        cityOptions.value = [];
      }
      try {
        const data = await SiteApi.getInstance().getStates(value);
        stateOptions.value = data.map((item) => ({
          label: item.text,
          value: item.value,
        }));
        if (stateOptions.value.length === 0) {
          city.value = "";
          cityOptions.value = [];
        }
      } catch (err) {
        console.error("Failed to load states:", err);
      }
    };
    const discard = () => {
      isVisible.value = false;
      emit("update:modelValue", false);
      siteUserTargetData.value = {};
      nextTick(() => {
        // formRef?.value?.resetForm();
      });
    };

    const submit = async (reqData) => {
      const addressModel = {
        street: reqData.street,
        street2:
          reqData.street2 && reqData.street2.length > 0 ? reqData.street2 : "",
        country: country.value,
        state: reqData.state,
        city: reqData.city,
        postalCode: reqData.postalCode,
      };
      emit("submit", addressModel);

      discard();
    };

    const hideDialog = () => {
      isVisible.value = false;
      emit("update:modelValue", false);
      closeButtonRef.value?.click();
    };

    const onDialogClosed = () => {
      formRef.value?.resetForm();
      addressLine1.value = "";
      addressLine2.value = "";
      city.value = "";
      state.value = "";
      zip.value = "";
      country.value = "";
      countryId.value = "";
      stateOptions.value = [];
      cityOptions.value = [];
    };

    watch(modelValueProp, async (val) => {
      if (val) {
        isVisible.value = val;
        // Reset form and options
        formRef.value?.resetForm();
        stateOptions.value = [];
        cityOptions.value = [];
        // Only treat as edit if dataProp.value is a non-empty object
        if (
          dataProp.value &&
          typeof dataProp.value === "object" &&
          Object.keys(dataProp.value).length > 0
        ) {
          addressLine1.value = dataProp.value["street"] || "";
          addressLine2.value = dataProp.value["street2"] || "";
          country.value = dataProp.value["country"] || "";
          state.value = dataProp.value["state"] || "";
          city.value = dataProp.value["city"] || "";
          zip.value = dataProp.value["postalCode"] || "";
        } else if (typeof dataProp.value === "string") {
          const newArray = dataProp.value.split(", ");
          addressLine1.value = newArray[0] || "";
          addressLine2.value = newArray[1] || "";
          country.value = newArray[2] || "";
          state.value = newArray[3] || "";
          city.value = newArray[4] || "";
          zip.value = newArray[5] || "";
        } else {
          addressLine1.value = "";
          addressLine2.value = "";
          country.value = "";
          state.value = "";
          city.value = "";
          zip.value = "";
          countryId.value = "";
          await setDefaultCountry();
        }
        // Load state and city options if country is selected
        if (country.value || countryId.value) {
          let selectedCountry: { label: any; value: string } | undefined;
          if (countryId.value) {
            selectedCountry = countryOptions.value.find(
              (c) => c.value === countryId.value
            );
          }
          if (!selectedCountry && country.value) {
            selectedCountry = countryOptions.value.find(
              (c) => c.label === country.value
            );
          }
          if (selectedCountry && selectedCountry.value) {
            country.value = selectedCountry.label;
            countryId.value = selectedCountry.value;
            await loadState(selectedCountry.value, true);
            if (state.value) {
              const selectedState = stateOptions.value.find(
                (c) => c.label == state.value
              );
              if (selectedState && selectedState.value) {
                await loadCity(selectedState.value);
              }
            }
          }
        } else {
          stateOptions.value = [];
          cityOptions.value = [];
        }
      }
    });

    watch(dataProp, async (val) => {
      if (val) {
        formRef?.value?.resetForm();
        if (val !== null || val !== undefined) {
          addressLine1.value = val["street"];
          addressLine2.value = val["street2"];
          country.value = val["country"];
          city.value = val["city"];
          state.value = val["state"];
          zip.value = val["postalCode"];
          const selectedCountry = countryOptions?.value.find(
            (c) => c.label == country.value
          );
          if (selectedCountry && selectedCountry.value) {
            countryId.value = selectedCountry.value;
            await loadState(selectedCountry.value, true);
          }
          const selectedState = stateOptions.value.find(
            (c) => c.label == state.value
          );
          if (selectedState && selectedState.value) {
            loadCity(selectedState.value);
          }
        } else {
          addressLine1.value = "";
          addressLine2.value = "";
          city.value = "";
          state.value = "";
          zip.value = "";
          countryId.value = "";
          await setDefaultCountry();
        }
      }
    });
    return {
      clientDetails,
      loading,
      hideModalRef,
      submit,
      discard,
      translate,
      userAccountId,
      siteId,
      userRoleId,
      t,
      contactDetailsValidation,
      formRef,
      userOptions,
      roleOptions,
      siteUserTargetData,
      modelValueProp,
      hideDialog,
      onDialogClosed,
      dataProp,
      countryOptions,
      addressLine1,
      addressLine2,
      city,
      state,
      zip,
      isVisible,
      country,
      countryId,
      initialValues,
      editable,
      changeCountry,
      stateOptions,
      changeState,
      cityOptions,
      convertToCapitalCase,
      setDefaultCountry,
    };
  },
});
